import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { dataService } from '../../../services/dataService';
import { LearningPath, Standard, Question, Student } from '../../../data/types';
import StudentLayout from '../../layouts/StudentLayout';
import { AuthContext } from '../../../App';
import LearningPhaseVideo from './LearningPhaseVideo';
import LearningPhaseQuiz from './LearningPhaseQuiz';
import LearningPhaseGame from './LearningPhaseGame';
import LearningPhaseResults from './LearningPhaseResults';
import { GameIcons } from '../../styles/GameIcons';
import '../../styles/GameStyleUI.css';
import './LearningPathCarousel.css';

// Define the learning phase types
export type LearningPhaseType = 'video' | 'quiz' | 'game' | 'results';

// Game-style Phase Indicator component
const PhaseIndicator: React.FC<{
  phase: LearningPhaseType,
  icon: React.ReactNode,
  isActive: boolean,
  isComplete: boolean
}> = ({ phase, icon, isActive, isComplete }) => {
  // Base styling
  let bgColor = "bg-gray-200";
  let textColor = "text-gray-500";
  let borderColor = "border-gray-300";
  let animationClass = "";
  
  // Determine styling based on state
  if (isActive) {
    bgColor = "bg-blue-500";
    textColor = "text-white";
    borderColor = "border-blue-300";
    animationClass = "animate-pulse";
  } else if (isComplete) {
    bgColor = "bg-green-500";
    textColor = "text-white";
    borderColor = "border-green-300";
  }
  
  return (
    <motion.div 
      whileHover={{ scale: 1.1 }}
      className={`relative flex-shrink-0 w-8 h-8 rounded-full ${bgColor} ${textColor} flex items-center justify-center shadow-md z-10 border-2 ${borderColor} ${animationClass}`}
      style={{ boxShadow: isActive ? "0 0 0 2px rgba(59, 130, 246, 0.5)" : "" }}
    >
      {isComplete ? (
        <GameIcons.Check size={16} color="#FFFFFF" />
      ) : (
        icon
      )}
      
      {/* Phase label */}
      <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs font-medium text-white bg-gray-800 bg-opacity-75 rounded px-1.5 py-0.5 whitespace-nowrap">
        {phase.charAt(0).toUpperCase() + phase.slice(1)}
      </div>
    </motion.div>
  );
};

const LearningPhaseScreen: React.FC = () => {
  const { pathId, standardId } = useParams<{ pathId: string; standardId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeRole } = React.useContext(AuthContext);
  const initializationRef = useRef(false);
  
  // State management
  const [currentPath, setCurrentPath] = useState<LearningPath | null>(null);
  const [currentStandard, setCurrentStandard] = useState<Standard | null>(null);
  const [currentStudent, setCurrentStudent] = useState<Student | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentPhase, setCurrentPhase] = useState<LearningPhaseType>('video');
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [preparing, setPreparing] = useState(true);
  const [phaseComplete, setPhaseComplete] = useState({
    video: false,
    quiz: false,
    game: false
  });
  const [pointsEarned, setPointsEarned] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize component - only runs once to set up the component
  useEffect(() => {
    // Use ref to prevent multiple initializations even across re-renders
    if (initializationRef.current) return;
    initializationRef.current = true;
    
    console.log('LearningPhaseScreen - Initializing component');
    localStorage.setItem('isStudentSession', 'true');
    
    if (activeRole && activeRole !== 'Student') {
      navigate('/student/login');
      return;
    }
    
    // Log parameters for debugging
    console.log('LearningPhaseScreen - Route params:', { pathId, standardId });
    console.log('LearningPhaseScreen - Location state:', location.state);
    
    // If URL params are missing, try to get them from localStorage
    let effectivePathId = pathId;
    let effectiveStandardId = standardId;
    
    if (!effectivePathId || !effectiveStandardId) {
      effectivePathId = localStorage.getItem('currentPathId') || '';
      effectiveStandardId = localStorage.getItem('currentStandardId') || '';
      console.log('Retrieved IDs from localStorage:', { effectivePathId, effectiveStandardId });
      
      // If we have IDs in localStorage but not in the URL, update the URL
      if (effectivePathId && effectiveStandardId && (!pathId || !standardId)) {
        navigate(`/student/learning-v2/${effectivePathId}/${effectiveStandardId}`, { 
          replace: true,
          state: { fromDashboard: true, timestamp: Date.now() }
        });
        return; // The navigation will trigger this effect again with proper URL params
      }
    }
    
    if (!effectivePathId || !effectiveStandardId) {
      console.error('Missing required parameters: pathId or standardId');
      setError('Missing required learning path or standard information');
      setLoading(false);
      setPreparing(false);
      return;
    }
    
    // Mark as initialized to prevent duplicate data fetching
    setIsInitialized(true);
    fetchData(effectivePathId, effectiveStandardId);
  }, [navigate, activeRole, pathId, standardId, location]);

  // Fetch required data
  const fetchData = async (effectivePathId: string, effectiveStandardId: string) => {
    try {
      setLoading(true); // Ensure loading state is set at the beginning
      setPreparing(true); // Show preparation screen
      
      if (!effectivePathId || !effectiveStandardId) {
        console.error('fetchData: Missing required parameters', { effectivePathId, effectiveStandardId });
        setError('Missing required learning path or standard information');
        setLoading(false);
        setPreparing(false);
        return;
      }
      
      console.log('Fetching data for learning phase with IDs:', { effectivePathId, effectiveStandardId });
      
      console.log('Fetching data for pathId:', effectivePathId, 'standardId:', effectiveStandardId);
      
      // Store IDs in localStorage to ensure persistence
      localStorage.setItem('currentPathId', effectivePathId);
      // Fetch all required data in parallel
      const [paths, standards, allQuestions, students] = await Promise.all([
        dataService.getLearningPaths(),
        dataService.getStandards(),
        dataService.getQuestions(),
        dataService.getStudents()
      ]);
      
      // Find the current path
      const path = paths.find(p => p.id === effectivePathId);
      if (!path) {
        console.error('Learning path not found:', effectivePathId);
        setError(`Learning path not found: ${effectivePathId}`);
        setLoading(false);
        setPreparing(false);
        return;
      }
      setCurrentPath(path);
      
      // Find the current standard
      const standard = standards.find(s => s.id === effectiveStandardId);
      if (!standard) {
        console.error('Standard not found:', effectiveStandardId);
        setError(`Standard not found: ${effectiveStandardId}`);
        setLoading(false);
        setPreparing(false);
        return;
      }
      setCurrentStandard(standard);
      
      // Filter questions for this standard
      const standardQuestions = allQuestions.filter(q => q.standardId === effectiveStandardId);
      if (standardQuestions.length === 0) {
        console.warn('No questions found for standard:', effectiveStandardId);
      }
      setQuestions(standardQuestions);
      
      // Get current student
      const studentId = localStorage.getItem('activeStudentId');
      if (!studentId) {
        console.error('No active student ID found');
        setError('No active student ID found. Please log in again.');
        setLoading(false);
        setPreparing(false);
        return;
      }
      
      const student = students.find(s => s.id === studentId);
      if (!student) {
        console.error('Student not found:', studentId);
        setError(`Student profile not found: ${studentId}`);
        setLoading(false);
        setPreparing(false);
        return;
      }
      setCurrentStudent(student);
      
      // Set the current phase based on student progress
      if (student.progress && student.progress[effectiveStandardId]) {
        const progress = student.progress[effectiveStandardId];
        
        if (progress.completedAt) {
          // If already completed, start from video but mark previous phases as complete
          setPhaseComplete({
            video: true,
            quiz: true,
            game: true
          });
          setCurrentPhase('video');
        } else if (progress.learningPhaseStatus) {
          // Set the appropriate phase based on progress
          switch (progress.learningPhaseStatus) {
            case 'PracticeCompleted':
              setPhaseComplete({ video: true, quiz: true, game: false });
              setCurrentPhase('game');
              break;
            case 'VideoCompleted':
              setPhaseComplete({ video: true, quiz: false, game: false });
              setCurrentPhase('quiz');
              break;
            default:
              setCurrentPhase('video');
              break;
          }
        }
      }
      
      // Only set loading to false after all state updates are complete
      setLoading(false);
      // Finally, set loading to false
      setLoading(false);
      
      // Short delay before removing the preparation screen for smoother transition
      setTimeout(() => {
        setPreparing(false);
        console.log('Learning phase ready to display content');
      }, 500);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load learning content. Please try again.');
      setLoading(false);
      setPreparing(false);
    }
  };

  // Phase transition handlers
  const handleVideoComplete = () => {
    setPhaseComplete(prev => ({ ...prev, video: true }));
    setCurrentPhase('quiz');
    setPointsEarned(prev => prev + 5); // Award points for completing video
  };

  const handleQuizComplete = (quizScore: { correct: number, total: number }) => {
    setScore(quizScore);
    setPhaseComplete(prev => ({ ...prev, quiz: true }));
    setCurrentPhase('game');
    
    // Calculate points based on quiz performance
    const scorePercent = (quizScore.correct / quizScore.total) * 100;
    let pointsForQuiz = 0;
    
    if (scorePercent >= 90) pointsForQuiz = 20;
    else if (scorePercent >= 70) pointsForQuiz = 15;
    else if (scorePercent >= 50) pointsForQuiz = 10;
    else pointsForQuiz = 5;
    
    setPointsEarned(prev => prev + pointsForQuiz);
  };

  const handleGameComplete = (gameScore: number) => {
    setPhaseComplete(prev => ({ ...prev, game: true }));
    setCurrentPhase('results');
    setPointsEarned(prev => prev + gameScore);
  };

  const handleExitLearningPhase = () => {
    navigate(`/student/dashboard`);
  };

  // Function to render the appropriate phase icon
  const renderPhaseIcon = (phase: LearningPhaseType, isActive: boolean, isComplete: boolean) => {
    const baseClasses = "w-10 h-10 flex items-center justify-center rounded-full text-xl";
    let classes = baseClasses;
    
    if (isActive) {
      classes += " bg-blue-500 text-white animate-pulse";
    } else if (isComplete) {
      classes += " bg-green-500 text-white";
    } else {
      classes += " bg-gray-200 text-gray-500";
    }
    
    switch (phase) {
      case 'video':
        return (
          <div className={classes}>
            🎬
          </div>
        );
      case 'quiz':
        return (
          <div className={classes}>
            ❓
          </div>
        );
      case 'game':
        return (
          <div className={classes}>
            🎮
          </div>
        );
      case 'results':
        return (
          <div className={classes}>
            🏆
          </div>
        );
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" 
        style={{
          background: 'linear-gradient(135deg, #4a9fff 0%, #2c7fff 100%)',
          backgroundSize: '200% 200%',
          animation: 'gradientBackground 15s ease infinite'
        }}>
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center game-panel bg-white bg-opacity-90 backdrop-filter backdrop-blur-sm p-8 rounded-2xl"
        >
          <motion.div 
            animate={{ 
              rotate: 360,
              scale: [1, 1.1, 1]
            }} 
            transition={{ 
              rotate: { duration: 2, repeat: Infinity, ease: "linear" },
              scale: { duration: 1.5, repeat: Infinity, ease: "easeInOut" }
            }}
            className="inline-block h-16 w-16 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 mb-4 flex items-center justify-center"
          >
            <GameIcons.Book size={32} color="#FFFFFF" />
          </motion.div>
          <h2 className="game-heading text-xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-500">
            Loading your learning adventure...
          </h2>
          <p className="text-gray-600 mt-2">Preparing something awesome for you!</p>
        </motion.div>
      </div>
    );
  }
  

  
  // Show preparation screen while setting up the learning phase
  if (preparing) {
    return (
      <div className="min-h-screen flex items-center justify-center" 
        style={{
          background: 'linear-gradient(135deg, #4a9fff 0%, #2c7fff 100%)',
          backgroundSize: '200% 200%',
          animation: 'gradientBackground 15s ease infinite'
        }}>
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center game-panel bg-white bg-opacity-90 backdrop-filter backdrop-blur-sm p-8 rounded-2xl"
        >
          <motion.div 
            animate={{ 
              y: [0, -10, 0],
              scale: [1, 1.05, 1]
            }} 
            transition={{ 
              y: { duration: 2, repeat: Infinity, ease: "easeInOut" },
              scale: { duration: 2, repeat: Infinity, ease: "easeInOut" }
            }}
            className="inline-block h-16 w-16 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 mb-4 flex items-center justify-center"
          >
            <GameIcons.Star size={32} color="#FFFFFF" />
          </motion.div>
          <h2 className="game-heading text-xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-500">
            Preparing your learning adventure...
          </h2>
          <p className="text-gray-600 mt-2">Building something magical for you!</p>
        </motion.div>
      </div>
    );
  }
  
  // Show error screen if something went wrong
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" 
        style={{
          background: 'linear-gradient(135deg, #4a9fff 0%, #2c7fff 100%)',
          backgroundSize: '200% 200%',
          animation: 'gradientBackground 15s ease infinite'
        }}>
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center game-panel bg-white bg-opacity-90 backdrop-filter backdrop-blur-sm p-8 rounded-2xl max-w-md"
        >
          <motion.div 
            animate={{ 
              rotate: [-5, 5, -5],
              scale: [1, 1.05, 1]
            }} 
            transition={{ 
              rotate: { duration: 2, repeat: Infinity, ease: "easeInOut" },
              scale: { duration: 1.5, repeat: Infinity, ease: "easeInOut" }
            }}
            className="inline-block h-16 w-16 rounded-full bg-gradient-to-br from-red-400 to-pink-500 mb-4 flex items-center justify-center"
          >
            <GameIcons.Pause size={32} color="#FFFFFF" />
          </motion.div>
          <h2 className="game-heading text-xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-red-600 to-pink-500">
            Oh no! Something went wrong
          </h2>
          <p className="text-gray-600 mt-2">{error}</p>
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/student/dashboard-v4')} 
            className="game-button mt-6 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg shadow-lg flex items-center justify-center mx-auto"
          >
            <GameIcons.Home size={20} className="mr-2" color="#FFFFFF" />
            Return to Dashboard
          </motion.button>
        </motion.div>
      </div>
    );
  }
  
  // Ensure we have all required data before rendering the main content
  if (!currentPath || !currentStandard || !currentStudent || questions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center" 
        style={{
          background: 'linear-gradient(135deg, #4a9fff 0%, #2c7fff 100%)',
          backgroundSize: '200% 200%',
          animation: 'gradientBackground 15s ease infinite'
        }}>
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center game-panel bg-white bg-opacity-90 backdrop-filter backdrop-blur-sm p-8 rounded-2xl max-w-md"
        >
          <motion.div 
            animate={{ 
              rotate: [0, 10, 0, -10, 0],
              scale: [1, 1.05, 1, 1.05, 1]
            }} 
            transition={{ 
              duration: 5, 
              repeat: Infinity, 
              ease: "easeInOut" 
            }}
            className="inline-block h-16 w-16 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 mb-4 flex items-center justify-center"
          >
            <GameIcons.Info size={32} color="#FFFFFF" />
          </motion.div>
          <h2 className="game-heading text-xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-yellow-600 to-amber-500">
            Missing Learning Content
          </h2>
          <p className="text-gray-600 mt-2">We couldn't find all the necessary content for this learning adventure.</p>
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/student/dashboard-v4')} 
            className="game-button mt-6 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg shadow-lg flex items-center justify-center mx-auto"
          >
            <GameIcons.Home size={20} className="mr-2" color="#FFFFFF" />
            Return to Dashboard
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" 
      style={{
        background: 'linear-gradient(135deg, #4a9fff 0%, #2c7fff 100%)',
        backgroundSize: '200% 200%',
        animation: 'gradientBackground 15s ease infinite'
      }}>
      {/* Animated background elements */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <motion.div 
          className="absolute h-64 w-64 rounded-full bg-blue-300 opacity-20"
          style={{ top: '10%', left: '5%' }}
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.3, 0.2]
          }}
          transition={{ 
            repeat: Infinity,
            duration: 8,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute h-48 w-48 rounded-full bg-indigo-300 opacity-20"
          style={{ top: '30%', right: '10%' }}
          animate={{ 
            scale: [1, 1.3, 1],
            opacity: [0.2, 0.25, 0.2]
          }}
          transition={{ 
            repeat: Infinity,
            duration: 10,
            ease: "easeInOut",
            delay: 2
          }}
        />
        <motion.div 
          className="absolute h-72 w-72 rounded-full bg-purple-300 opacity-10"
          style={{ bottom: '15%', left: '20%' }}
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.2, 0.1]
          }}
          transition={{ 
            repeat: Infinity,
            duration: 12,
            ease: "easeInOut",
            delay: 3
          }}
        />
      </div>

      {/* Main content container */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Compact title bar */}
        <motion.div 
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="bg-white bg-opacity-90 backdrop-filter backdrop-blur-sm shadow-lg px-4 py-2 z-20"
        >
          <div className="container mx-auto flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <motion.div 
                whileHover={{ rotate: 10, scale: 1.1 }}
                className="game-avatar-border w-10 h-10 flex-shrink-0"
              >
                <GameIcons.Book size={20} color="#FFFFFF" />
              </motion.div>
              <div className="flex flex-col">
                <h1 className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-indigo-500 font-bold text-lg truncate max-w-xs sm:max-w-sm md:max-w-md">
                  {currentPath?.name}: {currentStandard?.code}
                </h1>
                <p className="text-xs text-gray-600 truncate max-w-xs sm:max-w-sm md:max-w-md">{currentStandard?.description}</p>
              </div>
            </div>
            
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleExitLearningPhase}
              className="game-button bg-red-500 hover:bg-red-600 text-white text-sm font-bold py-1 px-3 rounded-lg flex items-center"
            >
              <GameIcons.Home size={16} className="mr-1" color="#FFFFFF" />
              Exit
            </motion.button>
          </div>
          
          {/* Enhanced Progress Tracker */}
          <div className="container mx-auto mt-2 mb-1">
            <div className="flex items-center w-full">
              {/* Video phase */}
              <PhaseIndicator 
                phase="video" 
                icon={<GameIcons.Play size={18} />} 
                isActive={currentPhase === 'video'} 
                isComplete={phaseComplete.video} 
              />
              
              {/* Enhanced connector line */}
              <motion.div 
                className="h-1.5 flex-grow mx-1 rounded-full overflow-hidden bg-gray-200"
                style={{ boxShadow: "inset 0 1px 2px rgba(0,0,0,0.1)" }}
              >
                <motion.div 
                  className="h-full bg-gradient-to-r from-green-400 to-green-500"
                  initial={{ width: "0%" }}
                  animate={{ width: phaseComplete.video ? "100%" : "0%" }}
                  transition={{ duration: 0.5 }}
                />
              </motion.div>
              
              {/* Quiz phase */}
              <PhaseIndicator 
                phase="quiz" 
                icon={<GameIcons.Question size={18} />} 
                isActive={currentPhase === 'quiz'} 
                isComplete={phaseComplete.quiz} 
              />
              
              {/* Enhanced connector line */}
              <motion.div 
                className="h-1.5 flex-grow mx-1 rounded-full overflow-hidden bg-gray-200"
                style={{ boxShadow: "inset 0 1px 2px rgba(0,0,0,0.1)" }}
              >
                <motion.div 
                  className="h-full bg-gradient-to-r from-green-400 to-green-500"
                  initial={{ width: "0%" }}
                  animate={{ width: phaseComplete.quiz ? "100%" : "0%" }}
                  transition={{ duration: 0.5 }}
                />
              </motion.div>
              
              {/* Game phase */}
              <PhaseIndicator 
                phase="game" 
                icon={<GameIcons.Gamepad size={18} />} 
                isActive={currentPhase === 'game'} 
                isComplete={phaseComplete.game} 
              />
              
              {/* Enhanced connector line */}
              <motion.div 
                className="h-1.5 flex-grow mx-1 rounded-full overflow-hidden bg-gray-200"
                style={{ boxShadow: "inset 0 1px 2px rgba(0,0,0,0.1)" }}
              >
                <motion.div 
                  className="h-full bg-gradient-to-r from-green-400 to-green-500"
                  initial={{ width: "0%" }}
                  animate={{ width: phaseComplete.game ? "100%" : "0%" }}
                  transition={{ duration: 0.5 }}
                />
              </motion.div>
              
              {/* Results phase */}
              <PhaseIndicator 
                phase="results" 
                icon={<GameIcons.Trophy size={18} />} 
                isActive={currentPhase === 'results'} 
                isComplete={false} 
              />
            </div>
          </div>
        </motion.div>
        
        {/* Main content area */}
        <div className="flex-grow flex items-center justify-center p-4">
          {/* Phase content */}
          <motion.div
            key={currentPhase}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.4 }}
            className="w-full max-w-5xl bg-white bg-opacity-95 backdrop-filter backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-blue-200"
            style={{ 
              boxShadow: "0 10px 25px -5px rgba(59, 130, 246, 0.5), 0 8px 10px -6px rgba(59, 130, 246, 0.3)"
            }}
          >
            {currentPhase === 'video' && (
              <div className="game-panel overflow-hidden">
                <LearningPhaseVideo 
                  standard={currentStandard}
                  onComplete={handleVideoComplete}
                />
              </div>
            )}
            
            {currentPhase === 'quiz' && (
              <div className="game-panel">
                <LearningPhaseQuiz 
                  questions={questions}
                  onComplete={handleQuizComplete}
                />
              </div>
            )}
            
            {currentPhase === 'game' && (
              <div className="game-panel">
                <LearningPhaseGame 
                  standard={currentStandard}
                  onComplete={handleGameComplete}
                />
              </div>
            )}
            
            {currentPhase === 'results' && (
              <div className="game-panel">
                <LearningPhaseResults 
                  score={score}
                  pointsEarned={pointsEarned}
                  standard={currentStandard}
                  student={currentStudent}
                  onExit={handleExitLearningPhase}
                />
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default LearningPhaseScreen;
